<template>
  <Fragment>
    <td class="px-1">
      <px-autocomplete-produto
        dense
        show-details
        menu-props="auto"
        :truncate-value="25"
        :clearable="false"
        v-model="produto.produto"
        class="rounded-tag-small"
        base-path="/produtos/page"
        :error="$v.produto.produto.$error"
        @blur="$v.produto.produto.$touch()"
        @change="onChange(produto)"/>
    </td>
    <td class="px-1">
      <px-input-decimal-number
        dense
        required
        ref="quantidade"
        label="Quantidade"
        class="required rounded-tag-small"
        :precision="getPrecision(produto.produto)"
        :error="$v.produto.quantidade.$error"
        @blur="$v.produto.quantidade.$touch()"
        v-model="produto.quantidade"/>
    </td>
    <td class="px-1">
      <px-autocomplete-unidade-medida
        dense
        required
        :clearable="false"
        :only-open="false"
        class="required rounded-tag-small"
        :error="$v.produto.unidadeMedida.id.$error"
        @blur="$v.produto.unidadeMedida.id.$touch()"
        v-model="produto.unidadeMedida"/>
    </td>
    <td class="px-1">
      <v-textarea
        outlined
        auto-grow
        dense
        hide-details
        v-model="produto.especificacaoProduto"
        label="Especificação"
        counter="1500"
        rows="1"
        class="rounded-tag-small"
        :error="$v.produto.especificacaoProduto.$error"
        @blur="$v.produto.especificacaoProduto.$touch()"/>
    </td>
    <td class="px-1">
      <px-cadastro-produto-observacoes @file-loading="onFileLoad"
                                       :is-edicao="isEdicao"
                                       :produto="produto"/>
      <px-cadastro-produto-campos-adicionais
        dense
        show-cadastros-adicionais
        ref="camposAdicionais"
        :produto="produto"
        cadastros-adicionais-type="requisicao-item"
        :cadastros-adicionais="produto.cadastrosAdicionais"
        :campos-obrigatorios="camposObrigatorios"/>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="duplicar(produto)">
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </template>
        <span>Copiar produto</span>
      </v-tooltip>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" @click="removeProduto">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </template>
        <span>Remover produto</span>
      </v-tooltip>
    </td>
  </Fragment>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import { getPrecision } from '@/utils/produto-utils';
import validationMixin from '@/mixins/validation-mixin';
import { Fragment } from 'vue-frag';
import {
  computeFrom,
  initialStateCamposErp,
  validateCamposErp,
} from '@/modules/requisicao/cadastro/campos-erp/validate-campos-erp';
import validationFileMixin from '@/mixins/validation-file-mixin';

export default {
  mixins: [validationMixin, validationFileMixin],
  props: {
    requisicao: {
      type: Object,
      required: true,
    },
    produto: {
      type: Object,
      required: true,
    },
    isEdicao: Boolean,
    camposObrigatorios: Object,
    erpCamposIntegracao: String,
  },
  methods: {
    validate() {
      const isFormInvalid = this.validateForm();
      const isCadastroProdutoCamposAdicionaisInvalid = this.$refs.camposAdicionais.validate();
      const isInvalid = (isFormInvalid || this.validateFile()
        || !this.validateCadastrosAdicionais()) || isCadastroProdutoCamposAdicionaisInvalid;
      return !isInvalid;
    },
    validateCadastrosAdicionais() {
      const [isValid, message] = validateCamposErp(this.erpCamposIntegracao,
        this.produto.cadastrosAdicionais);
      if (!isValid) {
        this.$toast(message, { color: 'error' });
      }
      return isValid;
    },
    onChange(produto) {
      this.produto.especificacaoProduto = this.getEspecificacao(produto);
      this.produto.unidadeMedida = this.getUnidadeMedida(produto);
      this.produto.arquivos = this.getArquivos(produto);
      this.produto.observacaoExterna = this.getObservacaoExterna(produto);
      this.produto.observacaoInterna = this.getObservacaoInterna(produto);
      this.inicializarCadastrosAdicionais();
    },
    getUnidadeMedida(produto) {
      return produto.produto ? { ...produto.produto.unidadeMedida } : null;
    },
    getEspecificacao(produto) {
      if (produto.especificacaoProduto) {
        return produto.especificacaoProduto;
      }
      return produto.produto ? produto.produto.especificacao : null;
    },
    getArquivos(produto) {
      if (produto.arquivos && produto.arquivos.length) {
        return produto.arquivos;
      }
      return produto.produto ? produto.produto.arquivos : [];
    },
    getObservacaoExterna(produto) {
      if (produto.observacaoExterna) {
        return produto.observacaoExterna;
      }
      return produto.produto ? produto.produto.observacaoExterna : null;
    },
    getObservacaoInterna(produto) {
      if (produto.observacaoInterna) {
        return produto.observacaoInterna;
      }
      return produto.produto ? produto.produto.observacaoInterna : null;
    },
    duplicar(item) {
      this.$emit('duplicar-produto', item);
    },
    removeProduto() {
      this.$emit('remove-produto');
    },
    inicializarCadastrosAdicionais() {
      if (this.produto.produto.cadastrosAdicionais
        && Array.isArray(this.produto.produto.cadastrosAdicionais)) {
        this.produto.cadastrosAdicionais = computeFrom(this.erpCamposIntegracao,
          this.produto.produto.cadastrosAdicionais);
      }
    },
  },
  computed: {
    getPrecision() {
      return item => getPrecision(item);
    },
  },
  created() {
    if (this.produto.cadastrosAdicionais && Array.isArray(this.produto.cadastrosAdicionais)) {
      this.produto.cadastrosAdicionais = computeFrom(this.erpCamposIntegracao,
        this.produto.cadastrosAdicionais);
      return;
    }
    if (!this.produto.cadastrosAdicionais) {
      this.produto.cadastrosAdicionais = initialStateCamposErp(this.erpCamposIntegracao);
    }
  },
  validations: {
    produto: {
      produto: {
        required: requiredIf(data => !data.especificacaoProduto),
      },
      especificacaoProduto: {
        required: requiredIf(data => !data.produto),
      },
      quantidade: {
        required,
      },
      unidadeMedida: {
        id: {
          required,
        },
      },
    },
  },
  components: {
    Fragment,
  },
};
</script>

<style scoped>
.produtos-table tr:hover:not(.v-table__expanded__content) {
  background: white !important;
}

</style>
